<script setup lang="ts">
import { computed, watch } from "vue";
import { requestMapLandmarkFocus } from "../lib/mapLandmarkFocus";
import { isJust, just, nothing, toMaybe } from "../lib/maybe";
import { manuscriptSceneSelectOptions } from "../lib/manuscriptScenes";
import { setEntitySceneLink } from "../lib/timelineEntities";
import { useStoryboardMapLocations } from "../composables/useStoryboardMapLocations";
import { useActiveEntityFieldsStore } from "../stores/useActiveEntityFieldsStore";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import { useSceneOpeningsStore } from "../stores/useSceneOpeningsStore";
import { useProjectWorkspaceStore } from "../stores/useProjectWorkspaceStore";
import { useTea } from "../lib/tea";
import { initialModel, update, type Cmd } from "./EntityScenesPanel.update";
import ZwButton from "./ui/ZwButton.vue";
import ZwDialog from "./ui/ZwDialog.vue";
import ZwSelect from "./ui/ZwSelect.vue";

const fields = useActiveEntityFieldsStore();
const session = useEditorSessionStore();
const openings = useSceneOpeningsStore();
const workspace = useProjectWorkspaceStore();
const { index: mapLocations } = useStoryboardMapLocations();
const scenes = computed(() =>
  manuscriptSceneSelectOptions(session.chapters, (path) =>
    openings.openingFor(path),
  ).filter((scene) => scene.value.length > 0),
);
const linkedScenes = computed(() =>
  scenes.value.flatMap((scene) => {
    const manual = fields.fields.sceneLinks.some(
      (link) => link.file === scene.value,
    );
    const activeEntry = session.activeDatabase;
    const map =
      isJust(fields.fields.type) &&
      fields.fields.type.value === "location" &&
      isJust(activeEntry)
        ? toMaybe(
            (mapLocations.value.get(scene.value) ?? []).find(
              (place) =>
                isJust(place.entityFile) &&
                place.entityFile.value === activeEntry.value,
            ),
          )
        : nothing;
    return manual || isJust(map) ? [{ ...scene, manual, map }] : [];
  }),
);
function runCmd(cmd: Cmd): void {
  if (cmd.kind === "OpenMap") {
    requestMapLandmarkFocus(cmd.mapFile, cmd.markerId);
    void workspace.onSelectProjectDatabase(cmd.mapFile);
    if (session.mobileShell) session.contextPanelOpen = false;
    return;
  }
  let next = fields.fields;
  if (cmd.kind === "Remove") next = setEntitySceneLink(next, cmd.scene, false);
  else {
    if (isJust(cmd.original))
      next = setEntitySceneLink(next, cmd.original.value, false);
    next = setEntitySceneLink(next, cmd.scene, true);
  }
  fields.setField("sceneLinks", next.sceneLinks, { immediate: true });
}
const { model, dispatch } = useTea(initialModel, update, {
  runCmd: just(runCmd),
});
watch(
  () => session.activeDatabase,
  () => dispatch({ type: "Close" }),
);
const availableScenes = computed(() => [
  { value: "", label: "None" },
  ...scenes.value.filter(
    (scene) =>
      scene.value === model.value.scene ||
      fields.fields.sceneLinks.some((link) => link.file === scene.value) ===
        false,
  ),
]);
const selected = computed({
  get: () => model.value.scene,
  set: (scene: string) => dispatch({ type: "Change", scene }),
});
</script>

<template>
  <section class="entity-scenes">
    <div class="entity-scenes-header">
      <h3>Scenes</h3>
      <ZwButton size="sm" @click="dispatch({ type: 'Create' })"
        >+ Add scene</ZwButton
      >
    </div>
    <p v-if="linkedScenes.length === 0">No linked scenes yet.</p>
    <ol v-else>
      <li v-for="(scene, index) in linkedScenes" :key="scene.value">
        <span class="scene-order">{{ index + 1 }}</span>
        <div class="scene-content">
          <button
            type="button"
            class="scene-link"
            @click="workspace.onSelectProjectFile(scene.value)"
          >
            {{ scene.label }}
          </button>
          <span v-if="isJust(scene.map)" class="scene-source">From map</span>
        </div>
        <button
          type="button"
          class="scene-edit"
          :aria-label="`Edit link to ${scene.label}`"
          :title="
            isJust(scene.map) ? 'Edit scene location on map' : 'Edit scene link'
          "
          @click="
            isJust(scene.map)
              ? dispatch({
                  type: 'EditMap',
                  mapFile: scene.map.value.mapFile,
                  markerId: scene.map.value.landmarkId,
                })
              : dispatch({ type: 'Edit', scene: scene.value })
          "
        >
          Edit
        </button>
      </li>
    </ol>
  </section>
  <ZwDialog
    :open="model.open"
    :title="isJust(model.original) ? 'Edit scene link' : 'Add scene'"
    :description="nothing"
    @close="dispatch({ type: 'Close' })"
  >
    <ZwSelect
      :id="nothing"
      v-model="selected"
      :label="just('Linked scene')"
      :options="just(availableScenes)"
    />
    <template #footer>
      <ZwButton
        v-if="isJust(model.original)"
        class="scene-remove"
        @click="dispatch({ type: 'Remove' })"
        >Remove</ZwButton
      >
      <ZwButton @click="dispatch({ type: 'Close' })">Cancel</ZwButton>
      <ZwButton
        variant="primary"
        :disabled="selected.length === 0"
        @click="dispatch({ type: 'Save' })"
        >{{ isJust(model.original) ? "Save scene" : "Add scene" }}</ZwButton
      >
    </template>
  </ZwDialog>
</template>

<style scoped>
.entity-scenes {
  margin-top: var(--space-md);
  padding-top: var(--space-md);
  border-top: 1px solid var(--line);
}
.entity-scenes-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
  margin-bottom: 0.35rem;
}
h3 {
  margin: 0;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
}
p {
  margin: 0 0.35rem;
  font-size: 0.85rem;
  line-height: 1.4;
  color: var(--text-muted);
}
ol {
  list-style: none;
  padding: 0;
  margin: 0;
}
li {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 0.35rem;
}
.scene-order,
.scene-source {
  color: var(--text-muted);
  font-size: 0.75rem;
}
.scene-content {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}
.scene-link {
  background: transparent;
  border: 0;
  cursor: pointer;
  text-align: left;
  padding: 0;
}
.scene-link {
  color: var(--text);
  font-size: 0.86rem;
  line-height: 1.35;
}
.scene-edit {
  margin-left: auto;
  background: transparent;
  border: 0;
  color: var(--text-muted);
  font-size: 0.75rem;
  cursor: pointer;
  opacity: 0;
}
li:hover .scene-edit,
li:focus-within .scene-edit {
  opacity: 1;
}
@media (pointer: coarse) {
  .scene-edit {
    opacity: 1;
  }
}
.scene-remove {
  margin-right: auto;
}
</style>
